// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size
import React, { ReactElement } from 'react';
import { CircleDollarSign, ClipboardCheck, CreditCard, Mail, ReceiptText, Waves } from 'lucide-react';

// Layouts
import AuthLayout from './layouts/Auth';
import DashboardLayout from './layouts/Dashboard';
import ErrorLayout from './layouts/Error';

// Auth components
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import PasswordResetRequest from './pages/auth/PasswordResetRequest';

// Errors
import NotFound from './pages/errors/NotFound';
import DomainNotFound from './pages/errors/DomainNotFound';
import UnhandledError from './pages/errors/UnhandledError';

//Dashboard
import InvoiceList from './pages/invoices/InvoiceList';
import PaymentList from './pages/payments/PaymentList';
import PaymentMethodList from './pages/paymentMethods/PaymentMethodList';
import CustomerProfile from './pages/customers/CustomerProfile';
import PasswordReset from './pages/auth/PasswordReset';
import UserProfile from './pages/account/UserProfile';
import ContactUs from './pages/companies/ContactUs';
import InviteRequest from './pages/auth/InviteRequest';
import RouteStopDetail from './pages/serviceHistory/RouteStopDetail';
import RouteStopList from './pages/serviceHistory/ServiceHistoryList';
import WorkOrderDetail from './pages/serviceHistory/WorkOrderDetail';
import InvalidInvite from './pages/errors/InvalidInvite';
import QuoteList from './pages/quotes/QuoteList';
import TestError from './pages/errors/TestError';

export interface NavRoute {
  path: string;
  // Path to use when evaluating whether the current customer has access to this item. If not present, the "path" is used for this purpose
  accessPath?: string;
  // Does this route appear in the sidenav
  sideNav?: boolean;
  // Text to show in the side nav
  navText?: string;
  // Icon to show in the side nav
  navIcon?: React.FC<any>;
  // Priority of this route when finding valid routes to show (hint takes precedence over the priority)
  priority?: number;
  // Role required for this route to be visible
  role?: string;
  // Hint regarding the preferred route to choose based on the current customer
  hint?: string;
  // Element to display
  element: ReactElement;
  // Child elements if applicable
  children?: NavRoute[];
}

export const routes: NavRoute[] = [
  {
    path: '/',
    element: <DashboardLayout />,
    children: [
      {
        path: 'service-history/stops/:routeStopId',
        accessPath: 'service-history/stops',
        role: 'ServiceHistory',
        element: <RouteStopDetail />,
      },
      {
        path: 'service-history/work-orders/:workOrderId',
        accessPath: 'service-history/work-orders',
        role: 'ServiceHistory',
        element: <WorkOrderDetail />,
      },
      {
        path: 'quotes',
        sideNav: true,
        navText: 'Quotes',
        navIcon: ClipboardCheck,
        priority: 120,
        element: <QuoteList />,
      },
      {
        path: 'service-history',
        sideNav: true,
        navText: 'Service History',
        navIcon: Waves,
        role: 'ServiceHistory',
        hint: 'ServiceHistory',
        priority: 10,
        element: <RouteStopList />,
      },
      {
        path: 'invoices',
        sideNav: true,
        navText: 'Invoices',
        navIcon: ReceiptText,
        priority: 20,
        hint: 'Invoices',
        element: <InvoiceList />,
      },
      {
        path: 'payments',
        sideNav: true,
        navText: 'Payments',
        navIcon: CircleDollarSign,
        priority: 110,
        element: <PaymentList />,
      },
      {
        path: 'payment-methods',
        sideNav: true,
        navText: 'Payment Methods',
        navIcon: CreditCard,
        priority: 1,
        hint: 'PaymentMethods',
        element: <PaymentMethodList />,
      },
      {
        path: 'contact-us',
        sideNav: true,
        navText: 'Contact Us',
        navIcon: Mail,
        priority: 100,
        element: <ContactUs />,
      },
      {
        path: 'customer-profile',
        priority: 200,
        element: <CustomerProfile />,
      },
      {
        path: 'user-profile',
        element: <UserProfile />,
      },
    ],
  },
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'sign-in',
        element: <SignIn />,
      },
      {
        path: 'sign-up/:inviteToken',
        element: <SignUp />,
      },
      {
        path: 'request-password-reset',
        element: <PasswordResetRequest />,
      },
      {
        path: 'reset-password',
        element: <PasswordReset />,
      },
      {
        path: 'request-invite',
        element: <InviteRequest />,
      },
    ],
  },
  {
    path: 'error',
    element: <ErrorLayout />,
    children: [
      {
        path: 'domain-not-found',
        element: <DomainNotFound />,
      },
      {
        path: 'invalid-invite',
        element: <InvalidInvite />,
      },
      {
        path: 'not-found',
        element: <NotFound />,
      },
      {
        path: 'unhandled-error',
        element: <UnhandledError />,
      },
      {
        path: 'test-error',
        element: <TestError />,
      },
    ],
  },
  {
    path: '*',
    element: <ErrorLayout />,
    children: [
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
